import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { tenantService } from '../../services/api';
import { translateDynamic, useLanguage } from '../../context/LanguageContext';
import { fitFloorToBox, floorTransform } from './floorGeometry';

const STATUS_COLORS = {
    available: '#10B981',      // Green
    seated: '#3B82F6',         // Blue — occupied, no order yet
    ordered: '#EF4444',        // Red — has pending order
    awaiting_bill: '#d97706',  // Amber
    paid: '#94a3b8',           // Grey
    selected: '#2563eb',       // Brand green
};

// Build a lookup of merged-table groups from active occupations.
// Returns { anchorByMember, groupByAnchor } where the anchor occupation lists
// its extra tables in `merged_table_numbers`.
export const buildTableGroups = (occupations = []) => {
    const anchorByMember = {};
    const groupByAnchor = {};
    (occupations || []).forEach(o => {
        const members = Array.isArray(o.merged_table_numbers)
            ? o.merged_table_numbers.map(String)
            : [];
        if (members.length > 0) {
            const anchorNum = String(o.table_number);
            groupByAnchor[anchorNum] = { occupation: o, members };
            members.forEach(m => { anchorByMember[String(m)] = o; });
        }
    });
    return { anchorByMember, groupByAnchor };
};

// Resolve a table number to the occupation that owns it — a direct occupation,
// or the anchor's occupation if the table is a merged member. Shared so every
// surface (waiter floor plan, cashier list/visual) resolves merges identically.
export const resolveOccupation = (occupations, tableNumber) => {
    const tn = String(tableNumber);
    const direct = (occupations || []).find(o => String(o.table_number) === tn);
    if (direct) return direct;
    return buildTableGroups(occupations).anchorByMember[tn] || null;
};

const getSeatedDuration = (seatedAt) => {
    if (!seatedAt) return '';
    const mins = Math.floor((Date.now() - new Date(seatedAt).getTime()) / 60000);
    if (mins <= 0) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h${rem}m` : `${hrs}h`;
};

const VisualTableSelector = ({
    selectedDiningOption,
    selectedTable,
    onTableSelect,
    onTableWithOrderClick = null,
    pendingOrders = {},
    printedOrders = {},
    diningOptions = {},
    onDataChange = null,
    isVisible = true,
    currency = '$',
    occupations = [],
    hideHeader = false,
    scaleToFit = false,
    mergeMode = false,
    mergeSelection = [],
}) => {
    const { t } = useLanguage();
    const { anchorByMember, groupByAnchor } = useMemo(
        () => buildTableGroups(occupations),
        [occupations]
    );
    const mergeSelStr = (mergeSelection || []).map(String);
    const [loading, setLoading] = useState(true);

    const CANVAS_WIDTH = 800;
    const CANVAS_HEIGHT = 500;

    const canvasContainerRef = useRef(null);
    const [canvasScale, setCanvasScale] = useState(1);
    // hideHeader mode only — fit-to-content transform (floorGeometry.js).
    const [canvasFit, setCanvasFit] = useState(() => fitFloorToBox([], 0, 0));
    // Latest parsed tables for recalcScale to read without a stale closure
    // (getCurrentTables is declared further down and depends on props).
    const currentTablesRef = useRef([]);

    const recalcScale = useCallback(() => {
        if (!canvasContainerRef.current) return;
        if (hideHeader) {
            const rect = canvasContainerRef.current.getBoundingClientRect();
            const sx = rect.width / CANVAS_WIDTH;
            const sy = rect.height / CANVAS_HEIGHT;
            setCanvasScale(Math.min(sx, sy, 1));
            setCanvasFit(fitFloorToBox(currentTablesRef.current, rect.width, rect.height));
        } else if (scaleToFit) {
            // Fit the full 800-wide coordinate space into the available width so
            // no tables get clipped on narrow screens (padding p-4 = 16px each side).
            const availW = canvasContainerRef.current.clientWidth - 32;
            setCanvasScale(Math.min(availW / CANVAS_WIDTH, 1));
        }
    }, [hideHeader, scaleToFit]);

    useEffect(() => {
        setLoading(false);
    }, []);

    useEffect(() => {
        // Recalculate after loading finishes and on resize
        if (!loading) {
            // Small delay to ensure DOM has rendered
            const timer = setTimeout(recalcScale, 50);
            return () => clearTimeout(timer);
        }
    }, [loading, recalcScale]);

    useEffect(() => {
        window.addEventListener('resize', recalcScale);
        return () => window.removeEventListener('resize', recalcScale);
    }, [recalcScale]);

    useEffect(() => {
        const el = canvasContainerRef.current;
        if (!hideHeader || !el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => recalcScale());
        ro.observe(el);
        return () => ro.disconnect();
    }, [hideHeader, recalcScale]);

    useEffect(() => {
        if (!hideHeader) return undefined;
        const timer = setTimeout(recalcScale, 50);
        return () => clearTimeout(timer);
    }, [hideHeader, recalcScale, selectedDiningOption, diningOptions]);

    useEffect(() => {
        const handleRefresh = () => {
            console.log('Refreshing dining options data...');
            if (onDataChange) {
                onDataChange();
            }
        };

        window.addEventListener('refreshVisualLayout', handleRefresh);

        if (isVisible && selectedDiningOption) {
            handleRefresh();
        }

        return () => {
            window.removeEventListener('refreshVisualLayout', handleRefresh);
        };
    }, [selectedDiningOption, isVisible]);

    const getCurrentTables = () => {
        console.log('getCurrentTables called for:', selectedDiningOption);
        console.log('Available dining options:', diningOptions);

        if (!selectedDiningOption || !diningOptions[selectedDiningOption]) {
            console.log('No dining option found for:', selectedDiningOption);
            return [];
        }

        const tablesData = diningOptions[selectedDiningOption];
        console.log('Tables data:', tablesData);

        if (Array.isArray(tablesData)) {
            // Legacy array-of-objects format
            const tables = tablesData
                .filter(table => typeof table === 'object' && table.number !== undefined)
                .map(table => ({
                    id: `table-${table.number}`,
                    number: table.number,
                    x: table.x || 200,
                    y: table.y || 200,
                    shape: table.shape || 'circle',
                    color: table.color || '#3B82F6',
                    size: table.size || 60,
                    capacity: table.capacity || 4,
                }));

            return tables;
        }

        if (typeof tablesData === 'object' && tablesData !== null) {
            // New JSON object format: {"1": [{x, y, shape, color, size}], "2": []}
            // x/y stored as percentages (0-1) in DB, convert to pixels for rendering
            return Object.entries(tablesData).map(([num, design]) => {
                const d = Array.isArray(design) && design.length > 0 ? design[0] : {};
                const idx = parseInt(num) - 1;
                let px = d.x, py = d.y;
                // Convert percentage coordinates to pixel space (800x500 canvas)
                if (px !== undefined && px <= 1) px = Math.round(px * 800);
                if (py !== undefined && py <= 1) py = Math.round(py * 500);
                return {
                    id: `table-${num}`,
                    number: parseInt(num),
                    x: px || 100 + (idx % 5) * 140,
                    y: py || 100 + Math.floor(idx / 5) * 120,
                    shape: d.shape || 'circle',
                    color: d.color || '#3B82F6',
                    size: d.size || 60,
                    capacity: d.capacity || 4,
                };
            }).sort((a, b) => a.number - b.number);
        }

        console.log('No visual data available for:', selectedDiningOption);
        return [];
    };

    const getOccupation = (tableNumber) => {
        if (!occupations || occupations.length === 0) return null;
        const tableNumStr = tableNumber.toString();
        // Direct occupation, or — if this table is merged into a group — the anchor's.
        return (
            occupations.find(o => o.table_number === tableNumStr) ||
            anchorByMember[tableNumStr] ||
            null
        );
    };

    const getTableStatus = (tableNumber) => {
        const tableNumStr = tableNumber.toString();
        const tableKey = `${selectedDiningOption}-${tableNumStr}`;

        // Check occupation status first (resolves merged members to their anchor)
        if (occupations && occupations.length > 0) {
            const occ = getOccupation(tableNumStr);
            if (occ) {
                if (occ.paid_at && !occ.cleared_at) return 'paid';
                if (occ.bill_requested_at && !occ.paid_at) return 'awaiting_bill';
                if (occ.status === 'active') {
                    if (pendingOrders[tableKey] || occ.first_order_at) return 'ordered';
                    return 'seated';
                }
            }
        }

        // Legacy: no occupation but has pending order
        if (pendingOrders[tableKey]) return 'ordered';

        if (selectedTable && selectedTable.toString() === tableNumStr) return 'selected';

        return 'available';
    };

    const getTableColor = (table) => {
        const tableNumStr = table.number.toString();
        const status = getTableStatus(tableNumStr);
        const tableKey = `${selectedDiningOption}-${tableNumStr}`;

        // If occupations prop is provided, use STATUS_COLORS map
        if (Array.isArray(occupations)) {
            return STATUS_COLORS[status] || STATUS_COLORS.available;
        }

        // Legacy fallback — keep original behaviour when occupations not provided
        const isPrinted = printedOrders[tableKey] === true;
        switch (status) {
            case 'ordered':
                return isPrinted ? '#a855f7' : '#EF4444';
            case 'selected':
                return '#10B981';
            default:
                return table.color || '#3B82F6';
        }
    };

    const handleTableClick = (table) => {
        const tableNumStr = table.number.toString();
        const status = getTableStatus(tableNumStr);
        const tableKey = `${selectedDiningOption}-${tableNumStr}`;

        // When hideHeader is true (TableServicePage mode), always delegate to parent
        // (the parent resolves merged members to the anchor itself).
        if (hideHeader) {
            onTableSelect(tableNumStr);
            return;
        }

        // For a merged member table, act on the anchor so callers never start a
        // new order on — or seat a guest onto — a table that belongs to a group.
        const occ = getOccupation(tableNumStr);
        const effNum = occ ? String(occ.table_number) : tableNumStr;
        const effKey = `${selectedDiningOption}-${effNum}`;

        if (status === 'ordered' || status === 'awaiting_bill' || status === 'paid') {
            if (onTableWithOrderClick && pendingOrders[effKey]) {
                onTableWithOrderClick(effNum, pendingOrders[effKey]);
            }
            return;
        }

        if (status === 'selected') {
            onTableSelect('');
        } else {
            onTableSelect(effNum);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!selectedDiningOption) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p>{t('select_dining_option_to_view_tables')}</p>
            </div>
        );
    }

    const currentTables = getCurrentTables();
    currentTablesRef.current = currentTables;

    // Coordinate lookup (table number → centre x/y) for drawing group connectors.
    const tableCoords = {};
    currentTables.forEach(tt => { tableCoords[String(tt.number)] = tt; });

    if (currentTables.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p>{t('no_visual_layout_configured')} {translateDynamic(selectedDiningOption, t)}</p>
                <p className="text-sm mt-2">{t('configure_layouts_in_settings')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" style={hideHeader ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}>
            {!hideHeader && (
                <div className="flex items-center gap-4 text-sm flex-wrap">
                        <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.available }}></div>
                            <span>{t('available')}</span>
                        </div>
                        {occupations && occupations.length > 0 && (
                            <>
                                <div className="flex items-center gap-1">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.seated }}></div>
                                    <span>{t('seated')}</span>
                                </div>
                            </>
                        )}
                        <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.ordered }}></div>
                            <span>{t('ordered')}</span>
                        </div>
                        {occupations && occupations.length > 0 && (
                            <>
                                <div className="flex items-center gap-1">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.awaiting_bill }}></div>
                                    <span>{t('awaiting_bill')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.paid }}></div>
                                    <span>{t('paid')}</span>
                                </div>
                            </>
                        )}
                        <div className="flex items-center gap-1">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: STATUS_COLORS.selected }}></div>
                            <span>{t('selected')}</span>
                        </div>
                </div>
            )}

            <div
                ref={canvasContainerRef}
                className={hideHeader ? '' : 'border rounded-lg p-4 bg-gray-50'}
                style={hideHeader
                    ? { flex: 1, overflow: 'hidden', position: 'relative' }
                    : (scaleToFit ? { overflow: 'hidden', position: 'relative', height: CANVAS_HEIGHT * canvasScale + 32 } : {})}
            >
                <div
                    className="relative bg-white border border-gray-200 rounded-lg mx-auto"
                    style={{
                        width: (hideHeader || scaleToFit) ? CANVAS_WIDTH : Math.min(CANVAS_WIDTH, window.innerWidth - 100),
                        height: (hideHeader || scaleToFit) ? CANVAS_HEIGHT : Math.min(CANVAS_HEIGHT, 500),
                        maxWidth: (hideHeader || scaleToFit) ? 'none' : '100%',
                        overflow: 'hidden',
                        ...(hideHeader ? {
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            transform: floorTransform(canvasFit),
                            transformOrigin: '0 0',
                        } : scaleToFit ? {
                            transform: `scale(${canvasScale})`,
                            transformOrigin: 'top left',
                        } : {}),
                        ...(scaleToFit && !hideHeader ? { marginLeft: 0, marginRight: 0 } : {}),
                    }}
                >
                    <svg className="absolute inset-0 pointer-events-none" width={CANVAS_WIDTH} height={CANVAS_HEIGHT}>
                        <defs>
                            <pattern id="salesGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f3f4f6" strokeWidth="0.5" />
                            </pattern>
                        </defs>
                        <rect width="100%" height="100%" fill="url(#salesGrid)" />

                        {/* Merged-group connector links: anchor → each member */}
                        {Object.entries(groupByAnchor).map(([anchorNum, group]) => {
                            const a = tableCoords[anchorNum];
                            if (!a) return null;
                            return group.members.map(memberNum => {
                                const m = tableCoords[memberNum];
                                if (!m) return null;
                                return (
                                    <line
                                        key={`link-${anchorNum}-${memberNum}`}
                                        x1={a.x} y1={a.y} x2={m.x} y2={m.y}
                                        stroke="#2563eb" strokeWidth="3"
                                        strokeOpacity="0.5" strokeLinecap="round"
                                    />
                                );
                            });
                        })}
                    </svg>

                    {currentTables.map(table => {
                        const status = getTableStatus(table.number);
                        const tableColor = getTableColor(table);
                        const isOccupied = status === 'ordered' || status === 'seated' || status === 'awaiting_bill' || status === 'paid';
                        const isSelected = status === 'selected';
                        const occ = getOccupation(table.number);
                        const seatedDuration = occ ? getSeatedDuration(occ.seated_at) : '';
                        const tableKey = `${selectedDiningOption}-${table.number}`;
                        const tableNumStr = String(table.number);
                        const groupHere = groupByAnchor[tableNumStr];
                        const isMergeSelected = mergeMode && mergeSelStr.includes(tableNumStr);
                        const isMergeAnchor = mergeMode && mergeSelStr[0] === tableNumStr;
                        // A merged member resolves to the anchor's occupation; flag it so we
                        // don't echo the anchor's party count as if it were this table's own.
                        const isMember = !!anchorByMember[tableNumStr] && !groupHere;

                        return (
                            <div
                                key={table.id}
                                className={`absolute cursor-pointer select-none flex flex-col items-center justify-center text-white font-bold text-xs transition-all duration-200 hover:scale-105 ${isMergeSelected ? 'border-[3px] border-emerald-500 ring-2 ring-emerald-300' :
                                    isSelected ? 'border-2 border-yellow-400 ring-2 ring-yellow-200' :
                                    isOccupied ? (printedOrders[tableKey] ? 'border-[3px] border-purple-500 ring-2 ring-purple-300' : 'border-2 border-white') : 'border-2 border-white hover:border-gray-300'
                                    }`}
                                style={{
                                    left: table.x - table.size / 2,
                                    top: table.y - table.size / 2,
                                    width: table.size,
                                    height: table.size,
                                    backgroundColor: tableColor,
                                    borderRadius: table.shape === 'circle' ? '50%' : '8px',
                                    boxShadow: isMergeSelected ? '0 0 0 3px rgba(16, 185, 129, 0.25)' : isSelected ? '0 0 0 3px rgba(34, 197, 94, 0.2)' : (printedOrders[tableKey] ? '0 0 0 2px rgba(168, 85, 247, 0.3)' : 'none'),
                                    position: 'absolute',
                                }}
                                onClick={() => handleTableClick(table)}
                                title={`${t('table')} ${table.number} - ${t(status)}${status === 'ordered' && printedOrders[tableKey] ? ` (${t('printed')})` : ''}`}
                            >
                                {/* Merge-mode anchor star */}
                                {isMergeAnchor && (
                                    <div style={{
                                        position: 'absolute', top: -10, right: -8,
                                        backgroundColor: '#2563eb', color: '#fff',
                                        fontSize: 10, fontWeight: 800, lineHeight: '16px',
                                        width: 16, height: 16, borderRadius: '50%',
                                        textAlign: 'center', pointerEvents: 'none',
                                    }}>★</div>
                                )}

                                {/* Group badge on the anchor of a merged group */}
                                {groupHere && !mergeMode && (
                                    <div style={{
                                        position: 'absolute', bottom: -11, left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: '#2563eb', color: '#fff',
                                        fontSize: 8, fontWeight: 800,
                                        borderRadius: 6, padding: '1px 5px',
                                        whiteSpace: 'nowrap', lineHeight: '14px',
                                        pointerEvents: 'none',
                                    }}>
                                        {`${t('group') || 'Group'} · ${occ ? occ.party_size : (groupHere.members.length + 1)}`}
                                    </div>
                                )}
                                {/* Time-seated badge for occupied tables (not member tiles — that's the anchor's time) */}
                                {occ && seatedDuration && !isMember && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: -10,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: 'rgba(0,0,0,0.65)',
                                            color: '#fff',
                                            fontSize: 8,
                                            fontWeight: 700,
                                            borderRadius: 6,
                                            padding: '1px 4px',
                                            whiteSpace: 'nowrap',
                                            lineHeight: '14px',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {seatedDuration}
                                    </div>
                                )}

                                <div className="text-center" style={{ lineHeight: 1.2 }}>
                                    {/* Table number */}
                                    <div style={{ fontSize: 13, fontWeight: 700 }}>{table.number}</div>

                                    {/* Member tiles show their anchor link, not the anchor's party count */}
                                    <div style={{ fontSize: 9, opacity: 0.85, marginTop: 1 }}>
                                        {isMember
                                            ? `↳ ${occ.table_number}`
                                            : (occ ? `${occ.party_size}/${table.capacity || 4}` : `${table.capacity || 4}`)}
                                    </div>

                                    {/* Legacy order info (no occupation prop) */}
                                    {status === 'ordered' && !occ && (
                                        <>
                                            <div style={{ fontSize: 9, marginTop: 2 }}>
                                                {pendingOrders[tableKey]?.cashier || t('staff')}
                                            </div>
                                            <div style={{ fontSize: 9 }}>
                                                {currency}{pendingOrders[tableKey]?.total?.toFixed(2)}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {!hideHeader && selectedTable && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-blue-800">
                        <span className="font-medium">{t('selected')}:</span> {t('table')} {selectedTable}
                    </p>
                </div>
            )}

            {!hideHeader && Object.keys(pendingOrders).length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">
                        <span className="font-medium">{t('tables_with_orders')}:</span>{' '}
                        {Object.keys(pendingOrders)
                            .filter(key => key.startsWith(selectedDiningOption))
                            .map(key => key.split('-')[1])
                            .join(', ')
                        }
                    </p>
                </div>
            )}
        </div>
    );
};

export default VisualTableSelector;
